import { appointmentSlot, inspectionTitle, type AssignedInquiry } from '@ghp/domain'
import { useToast } from '@ghp/ui'
import { useQueryClient } from '@tanstack/react-query'

import { useConnectionStatus, type ConnectionStatus } from '../../app/connection'
import { useTeamData } from '../../app/use-team-data'
import type { AuthStore } from '../auth/auth-store'
import { recordAppointment, type OnSiteAppointment } from '../../offline/outbox'
import { INQUIRIES_QUERY_KEY } from '../../offline/query-keys'

/**
 * Le geste « Planifier le rendez-vous » de la pré-fiche (Terrain 0.9b, #268), **le même depuis la
 * liste et depuis l'agenda** — comme l'avis de passage, et pour la même raison.
 *
 * ⚠️ **Rien n'attend le réseau** : le rendez-vous s'écrit en local, se montre aussitôt sur la
 * tournée et l'agenda, et part quand il peut.
 *
 * Le titre est celui de la frame (« Rendez-vous enregistré ») ; le détail dit ce qui est vrai
 * ({@link appointmentToastDetail}).
 */
export function useFieldAppointment(
  auth: AuthStore,
  requestSync: () => Promise<void>,
): (inquiry: AssignedInquiry, appointment: OnSiteAppointment) => void {
  const data = useTeamData()
  const queryClient = useQueryClient()
  const toast = useToast()
  const connection = useConnectionStatus(auth)

  return (inquiry, appointment) => {
    void recordAppointment(data, inquiry.id, appointment).then(
      async () => {
        const slot = appointmentSlot(appointment.scheduledAt)
        const when = slot === null ? appointment.scheduledAt : `${slot.day} · ${slot.time}`
        const where = `${inspectionTitle(inquiry, { short: true })} · ${when}`

        toast('Rendez-vous enregistré', appointmentToastDetail(where, connection))

        // La tournée et l'agenda lisent la même clé : le rendez-vous y paraît sans attendre.
        await queryClient.invalidateQueries({ queryKey: INQUIRIES_QUERY_KEY })
        await requestSync()
      },
      (error: unknown) => {
        console.error('[sync] rendez-vous non enregistré sur la tablette', error)
        toast('Rendez-vous non enregistré', 'L’écriture sur la tablette a échoué — réessayez')
      },
    )
  }
}

/**
 * Le détail du toast. ⚠️ **« partira », jamais « transmis »**, même en ligne (revue de #370) :
 * l'envoi part à la passe suivante et le serveur peut encore le refuser — le bureau a pu poser un
 * autre créneau. Un refus se lit ensuite dans Notifications.
 */
export function appointmentToastDetail(where: string, connection: ConnectionStatus): string {
  return connection === 'online'
    ? `${where} · partira au back-office`
    : `${where} · partira au retour du réseau`
}
